/**
 * The client's end of the wire.
 *
 * Holds the wire, keeps what the far end has said, and hands the game a
 * plain object to read. Nothing here knows what a chunk means or how a player
 * is drawn - it is the postbox, and main.ts does the reading.
 *
 * **It does not know what it is talking down.** A `Wire` is our server over
 * a WebSocket or another player's browser over a data channel, and every
 * line below this one is the same either way. That is the whole of what
 * being able to host a world in a page cost the client.
 *
 * **Joining reloads the page.** The world is grown at startup from a seed,
 * and the server has its own; rebuilding the world, its chunk meshes and the
 * worker pool in flight would be a great deal of machinery for something
 * nobody does in a hurry. So «З'єднатись» writes the address down and
 * reloads, and the game comes up online - the same blunt answer loading a
 * save already uses, for the same reason.
 *
 * **Everything fails to single player.** A server that is not there, a
 * refused version, a socket that drops halfway: the game must still start.
 * Being unable to play with somebody is a disappointment; being unable to
 * play at all because of it would be a bug.
 */

import type { Wire } from "../core/net/link.js";
import { socketWire } from "./socketWire.js";
import {
  PROTOCOL_VERSION, INPUT_HZ, decodeChunk,
  type ClientMessage, type ServerMessage,
  type PlayerState, type OrcState, type BlockEdit, type WorldSeed,
  type DroneState, type BeastState, type FishState, type VillagerState,
  type BoatState, type TankState,
  type CreatureKind, type BagState,
} from "../core/net/protocol.js";

/** Where the address waits across the reload that joins a server. */
const JOIN_KEY = "vyzhyvannia.join";
const WORD_KEY = "vyzhyvannia.joinWord";
/**
 * And which of the server's worlds.
 *
 * It has to survive the reload with the address, because the choice is made
 * on the home screen and the world is grown on the way back up - by the time
 * the page is running there is nobody left to ask.
 */
const WORLD_KEY = "vyzhyvannia.joinWorld";
/** The name to go in under, remembered so it is typed once and not always. */
const NAME_KEY = "vyzhyvannia.playerName";
/**
 * The name-tags each world has given us, by address and world.
 *
 * Per world, because a name is claimed in a world and the same name on two
 * servers is two different people. One blob rather than a key each so that
 * clearing it is one thing to clear.
 */
const TAGS_KEY = "vyzhyvannia.nameTags";

/** The password to go in with, kept so it is typed once and not always. */
const PASS_KEY = "vyzhyvannia.playerPass";

export function savedPassword(): string {
  try { return localStorage.getItem(PASS_KEY) ?? ""; } catch { return ""; }
}

/**
 * Kept in the browser, which is what "stay logged in" means and costs.
 *
 * Anybody who can read this storage can log in as you - the same as every
 * other "remember me" there has ever been, and the same trust the name-tag
 * already asked for. Cleared by typing nothing into the box.
 */
export function rememberPassword(password: string): void {
  try {
    if (password.length === 0) localStorage.removeItem(PASS_KEY);
    else localStorage.setItem(PASS_KEY, password);
  } catch { /* no storage */ }
}

export function savedName(): string {
  try { return localStorage.getItem(NAME_KEY) ?? ""; } catch { return ""; }
}

export function rememberName(name: string): void {
  try { localStorage.setItem(NAME_KEY, name); } catch { /* no storage */ }
}

function tags(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TAGS_KEY);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? parsed as Record<string, string> : {};
  } catch {
    return {};
  }
}

const tagKey = (url: string, world: string, name: string): string =>
  `${url}|${world}|${name}`;

/** The tag this world gave us for that name, if it ever did. */
export function nameTag(url: string, world: string, name: string): string {
  return tags()[tagKey(url, world, name)] ?? "";
}

export function keepNameTag(
  url: string, world: string, name: string, token: string,
): void {
  try {
    const all = tags();
    all[tagKey(url, world, name)] = token;
    localStorage.setItem(TAGS_KEY, JSON.stringify(all));
  } catch { /* no storage */ }
}

/**
 * And the one that says "come up holding a world", not joining one.
 *
 * The world a page holds lives in the page, and joining reloads the page -
 * so a world made on the home screen and held there would die on the way to
 * itself. What crosses the reload is the *intention*; the world is made on
 * the other side of it, once, by the page that is going to keep it.
 */
/**
 * Who the page is signed in as on this server: the remembered name, if a
 * token was kept for it. Signing in is done on the home screen and holds
 * until signed out or the server stops knowing the token.
 */
export function signedInAs(url: string): string {
  const name = savedName();
  return name.length > 0 && nameTag(url, "", name).length > 0 ? name : "";
}

export function signOut(url: string): void {
  const name = savedName();
  try {
    const all = tags();
    delete all[tagKey(url, "", name)];
    localStorage.setItem(TAGS_KEY, JSON.stringify(all));
  } catch { /* no storage */ }
  rememberPassword("");
}

const HOLD_KEY = "vyzhyvannia.hold";

/** Come up next time holding a world of that map, rather than joining one. */
/** «Сучасна війна» only: bots or not, and seconds of preparing. */
export interface MatchSettings {
  readonly bots: boolean; readonly prep: number;
  readonly hardcore?: boolean;
  /** Seconds the whole match may last. */
  readonly limit?: number;
}

export function holdOnReload(
  url: string, map: string, word: string, match?: MatchSettings,
): void {
  try {
    localStorage.setItem(HOLD_KEY, JSON.stringify({ url, map, word, ...(match ? { match } : {}) }));
  } catch {
    // Storage refused. Nothing to do but stay where we are.
  }
}

/** The world we are meant to be holding, if any. Read once, then forgotten. */
export function takeHoldRequest():
  { url: string; map: string; word: string; match?: MatchSettings } | null {
  try {
    const raw = localStorage.getItem(HOLD_KEY);
    /*
     * Cleared as soon as it is read, exactly as a join request is: a world
     * that refuses to come up must not leave somebody unable to reach their
     * own single-player game on every reload afterwards.
     */
    localStorage.removeItem(HOLD_KEY);
    if (raw === null) return null;
    const said = JSON.parse(raw) as Record<string, unknown>;
    if (typeof said.url !== "string" || typeof said.map !== "string") {
      return null;
    }
    const m = said.match as Record<string, unknown> | undefined;
    const match = m !== undefined && typeof m.bots === "boolean" &&
      typeof m.prep === "number" && m.prep > 0
      ? {
        bots: m.bots, prep: m.prep, hardcore: m.hardcore === true,
        ...(typeof m.limit === "number" && m.limit > 0 ? { limit: m.limit } : {}),
      } : undefined;
    return {
      url: said.url, map: said.map,
      word: typeof said.word === "string" ? said.word : "",
      ...(match !== undefined ? { match } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Which worlds are on somebody's own computer rather than on the server.
 *
 * Carried across the reload with the rest, because it decides *how* we go
 * in: down a WebSocket to the machine serving this page, or through an
 * introduction to another browser. Guessing it from the world's name would
 * work until somebody named a world badly.
 */
const GUEST_KEY = "vyzhyvannia.joinGuest";

/** Remember an address and come up online next time. */
export function joinOnReload(
  url: string, word: string, world = "", guest = false,
): void {
  try {
    localStorage.setItem(JOIN_KEY, url);
    localStorage.setItem(WORD_KEY, word);
    localStorage.setItem(WORLD_KEY, world);
    localStorage.setItem(GUEST_KEY, guest ? "1" : "");
  } catch {
    // Storage refused. Nothing to do but stay where we are.
  }
}

/** The address we are meant to join, if any. Read once, then forgotten. */
export function takeJoinRequest():
  { url: string; word: string; world: string; guest: boolean } | null {
  try {
    const url = localStorage.getItem(JOIN_KEY);
    const word = localStorage.getItem(WORD_KEY) ?? "";
    const world = localStorage.getItem(WORLD_KEY) ?? "";
    const guest = localStorage.getItem(GUEST_KEY) === "1";
    /*
     * Cleared as soon as it is read.
     *
     * A server that refuses us, or one that has gone away, must not leave the
     * game trying to join it on every reload for ever - which would be a
     * player who cannot get back into their own single-player world.
     */
    localStorage.removeItem(JOIN_KEY);
    localStorage.removeItem(WORD_KEY);
    localStorage.removeItem(WORLD_KEY);
    localStorage.removeItem(GUEST_KEY);
    return url === null ? null : { url, word, world, guest };
  } catch {
    return null;
  }
}

export interface JoinedWorld {
  readonly you: number;
  /** Which of the server's worlds we landed in. Its id, not its map's. */
  readonly worldId: string;
  readonly world: WorldSeed;
  /** What hour that world is at. One clock for everybody in it. */
  readonly time: number;
}

/** A chunk the server sent, waiting to be laid into the world. */
export interface IncomingChunk {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  readonly blocks: Uint16Array;
}

/** What the server says about a match. Mirrors the `war` message. */
export interface WarState {
  readonly phase: "lobby" | "prepare" | "fight" | "over";
  readonly left: number;
  readonly teams: readonly {
    readonly id: number; readonly name: string; readonly bots: boolean;
    readonly members: readonly string[]; readonly alive: boolean;
  }[];
  /** The team the page's own player is on, or -1. */
  readonly you: number;
  /** Whether the page's own player is the one who made the world. */
  readonly owner: boolean;
  /** Supplies by every flag. See `WAR_KIT`. */
  readonly hardcore?: boolean;
  /** Seconds until the world ends, and whether it has for want of time. */
  readonly ends?: number;
  readonly timeUp?: boolean;
  readonly scores?: readonly {
    readonly name: string; readonly team: number;
    readonly kills: number; readonly deaths: number;
  }[];
}

export class Connection {
  private wire: Wire | null = null;
  private sentAt = 0;

  /** Everybody else, as the server last described them. */
  players: PlayerState[] = [];
  /**
   * Every orc, as the server last described them.
   *
   * A list rather than a stream of events, for the same reason players are:
   * a snapshot that arrives late is still the truth, whereas a missed "orc 12
   * moved" leaves it standing in a doorway for ever.
   */
  orcs: OrcState[] = [];
  /**
   * When the last snapshot landed, in seconds.
   *
   * What the smoothing needs: bodies are drawn between the two places they
   * were last seen, and "when were they seen" is this. Wall-clock rather
   * than a tick count, because the gap between snapshots is what is being
   * interpolated across and a dropped one makes that gap longer.
   */
  arrivedAt = 0;
  /** What this world was keeping for us, if it had anything. See growWorld. */
  keptBag: BagState | null = null;
  /**
   * The herd, the карасі and the villagers near enough to be told about.
   *
   * Only what is close: none of them has ever been a threat, so learning
   * about a pig when you come over the hill loses nothing.
   */
  beasts: BeastState[] = [];
  fish: FishState[] = [];
  folk: VillagerState[] = [];
  /** The човни near enough to see, and who is in each. */
  boats: BoatState[] = [];
  /** And the танки, which arrive with them. */
  tanks: TankState[] = [];
  /** True once the server has welcomed us. */
  joined: JoinedWorld | null = null;
  /** Why we are not playing together, if we are not. */
  trouble: "none" | "unreachable" | "mismatch" | "wrong-word" | "taken-name"
    | "late"
    | "dropped" = "none";

  /** Set by main.ts before connecting. */
  onChunk: ((chunk: IncomingChunk) => void) | null = null;
  onBlocks: ((edits: readonly BlockEdit[]) => void) | null = null;
  onRaised: ((raised: {
    building: string; facing: number; x: number; y: number; z: number;
  }) => void) | null = null;
  /** Somebody else threw one, so it can be watched rather than only felt. */
  onBolt: ((bolt: {
    x: number; y: number; z: number; dx: number; dy: number; dz: number;
    /** True for Чахлик's cold fire, absent for a трeбушет's burning stone. */
    cold?: boolean;
  }) => void) | null = null;
  /** And somebody else struck a spark, so the same cell catches here too. */
  onSpark: ((at: { x: number; y: number; z: number }) => void) | null = null;
  /** The world's own hour, now and then. Ours is set to it. */
  onSky: ((time: number) => void) | null = null;
  /** "You are actually here" - the server would not believe where we said. */
  onWhere: ((at: {
    x: number; y: number; z: number;
    /** And this is where you come back to now. See the protocol. */
    home?: boolean;
  }) => void) | null = null;
  /**
   * Something got us.
   *
   * The server says only *that* we were hit and how hard; what our armour did
   * about it is still ours to work out, because armour, falling and drowning
   * are all still the body's own business and a health bar with two owners is
   * a health bar that flickers.
   */
  onHurt: ((amount: number, from?: "player", by?: number) => void) | null = null;
  /**
   * What the шопопало-блок gave you, on «Один блок».
   *
   * Only ever sent to the one who hit it: the count and the roll are the
   * world's, the bag is yours. A creature never arrives this way - the
   * server puts one into the world and it comes with the herd.
   */
  onGave: ((item: number, count: number) => void) | null = null;
  /** Somebody loosed an arrow. Drawn, and nothing more - see the protocol. */
  onArrow: ((arrow: {
    x: number; y: number; z: number; dx: number; dy: number; dz: number;
  }) => void) | null = null;
  /** An orc went down, and what the server rolled off it. */
  onFell: ((fell: {
    x: number; y: number; z: number;
    drops: readonly { item: number; count: number }[];
  }) => void) | null = null;
  /** A вибухоблок went off somewhere. The blocks arrive as ordinary edits. */
  onBoom: ((at: {
    x: number; y: number; z: number;
    /** A танк going up rather than a вибухоблок. See the protocol. */
    tank?: boolean;
  }) => void) | null = null;
  /** Somebody else's shell, to be drawn and flown on this page too. */
  onShell: ((shell: {
    x: number; y: number; z: number;
    vx: number; vy: number; vz: number; from: number;
  }) => void) | null = null;
  /** Who is on top of the hill, and how long they have held it. */
  onKing: ((king: {
    id: number; name: string; held: number; over: boolean;
    // Where the round has got to: a minute of gathering, then five to climb.
    phase?: "gather" | "climb"; left?: number;
  }) => void) | null = null;
  /**
   * Where a match of «Сучасна війна» has got to.
   *
   * One callback for the lobby, the clock, the fighting and the table at
   * the end: the page has one screen for all four, and which one it is
   * showing is `phase`.
   */
  onWar: ((war: WarState) => void) | null = null;
  onTrouble: ((why: string) => void) | null = null;
  /** The server said no to something we asked for, while we are still in. */
  onRefused: ((code: string, why: string) => void) | null = null;

  get online(): boolean {
    return this.wire !== null && this.wire.open() && this.joined !== null;
  }

  /**
   * Open the connection and wait to be welcomed.
   *
   * Resolves with the world to grow, or null if we could not get in - and
   * `trouble` says which, because "nobody home" and "wrong version" want
   * different things from the player.
   */
  async connect(
    url: string, name: string, skinId: string, word: string, world: string,
    /** Only when the name has an account. Guests send none. */
    password = "",
  ): Promise<JoinedWorld | null> {
    const wire = await socketWire(url);
    if (wire === null) {
      this.trouble = "unreachable";
      return null;
    }
    return this.join(wire, url, name, skinId, word, world, password);
  }

  /**
   * Go in down a wire somebody else opened.
   *
   * The same hello, the same answers, the same everything: what is on the
   * far end may be our server over a WebSocket or another player's browser
   * over a data channel, and nothing below this line can tell. That is the
   * whole of what being able to host a world in a page cost the client.
   *
   * `at` is only a name to file the world's tag under - a world remembers
   * who you are between visits, and it has to be filed per place.
   */
  join(
    wire: Wire, at: string, name: string, skinId: string, word: string,
    world: string, password = "", ticket = "",
  ): Promise<JoinedWorld | null> {
    // The tag this world gave us last time we came in under this name - or
    // the one signing in on the home screen gave us, for a first time.
    const token = nameTag(at, world, name) || nameTag(at, "", name);
    const url = at;
    return new Promise((resolve) => {
      this.wire = wire;

      let settled = false;
      const give = (result: JoinedWorld | null): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(result);
      };
      const timer = setTimeout(() => {
        this.trouble = "unreachable";
        give(null);
      }, 6000);

      /*
       * Naming the world is what asks to be *put* in one.
       *
       * A hello without it is a knock: the far end answers with its list and
       * grows nothing. That is the probe's business, on the home screen, and
       * by the time we are here the choice has been made.
       */
      wire.send({
        t: "hello", v: PROTOCOL_VERSION, name, skinId, word, world,
        ...(token.length > 0 ? { token } : {}),
        ...(password.length > 0 ? { password } : {}),
        ...(ticket.length > 0 ? { ticket } : {}),
      });

      wire.onChunk = (frame): void => {
        const chunk = decodeChunk(frame);
        if (chunk !== null) this.onChunk?.(chunk);
      };

      wire.onMessage = (raw): void => {
        const message = raw as ClientMessage | ServerMessage;
        switch (message.t) {
          case "welcome":
            /*
             * Keep the tag before anything else.
             *
             * It is the only copy: the server hands it over once, when the
             * name is claimed, and losing it means the name is somebody
             * else's for ever - including yours.
             */
            keepNameTag(url, world, name, message.token);
            this.joined = {
              you: message.you, worldId: message.worldId, world: message.world,
              time: message.time,
            };
            give(this.joined);
            return;
          case "no":
            /*
             * A refusal after we are in is about what we just asked for.
             *
             * Before this, every `no` was read as the connection failing, so
             * a building that would not fit raised a permanent "зв'язок
             * обірвано" over a game that was working perfectly. Being told
             * "not there" is not being told to go away.
             */
            if (this.joined !== null) {
              this.onRefused?.(message.code, message.why);
              return;
            }
            this.trouble = message.code === "word" ? "wrong-word"
              : message.code === "name" ? "taken-name"
              : message.code === "late" ? "late" : "mismatch";
            this.onTrouble?.(message.why);
            give(null);
            return;
          case "players":
            this.players = [...message.list];
            this.arrivedAt = performance.now() / 1000;
            return;
          case "orcs":
            this.orcs = [...message.list];
            this.arrivedAt = performance.now() / 1000;
            return;
          case "herd":
            this.arrivedAt = performance.now() / 1000;
            this.beasts = [...message.beasts];
            this.fish = [...message.fish];
            this.folk = [...message.folk];
            this.boats = [...message.boats];
            this.tanks = [...(message.tanks ?? [])];
            return;
          case "hurt":
            this.onHurt?.(message.amount, message.from, message.by);
            return;
          case "gave":
            this.onGave?.(message.item, message.count);
            return;
          case "arrow":
            this.onArrow?.(message);
            return;
          case "fell":
            this.onFell?.({
              x: message.x, y: message.y, z: message.z,
              drops: message.drops.map((d) => ({ item: d.item, count: d.count })),
            });
            return;
          case "blocks":
            this.onBlocks?.(message.edits);
            return;
          case "raised":
            this.onRaised?.(message);
            return;
          case "bolt":
            this.onBolt?.(message);
            return;
          case "spark":
            this.onSpark?.(message);
            return;
          case "sky":
            this.onSky?.(message.time);
            return;
          case "where":
            this.onWhere?.(message);
            return;
          case "boom":
            this.onBoom?.(message);
            return;
          case "shell":
            this.onShell?.(message);
            return;
          case "king":
            this.onKing?.(message);
            return;
          case "war":
            this.onWar?.(message);
            return;
          case "bag":
            /*
             * Kept, not announced.
             *
             * It arrives on the heels of the welcome - before the bag it is
             * meant to fill exists, because the world is grown after the
             * connection is made. A callback here would fire into nothing.
             */
            this.keptBag = message.bag;
            return;
          case "bye":
            this.players = this.players.filter((p) => p.id !== message.id);
            return;
          default:
            return;
        }
      };

      wire.onClose = (): void => {
        // Dropping after we were in is a different thing from never getting
        // in, and the player should be told which happened.
        if (settled && this.joined !== null) {
          this.trouble = "dropped";
          this.joined = null;
          this.players = [];
          this.orcs = [];
          this.beasts = []; this.fish = []; this.folk = [];
          this.onTrouble?.("dropped");
        }
        give(null);
      };
    });
  }

  /**
   * Say where we are, no more often than the protocol asks for.
   *
   * Rate-limited here rather than at the call site: the frame loop runs as
   * fast as the machine will go, and a message a frame would be sixty a
   * second of the same thing.
   */
  move(
    at: { x: number; y: number; z: number }, yaw: number, pitch: number,
    // Their drone travels on the same message as their body: the two move
    // together, and one arriving on a different beat would jitter against the
    // person flying it.
    drone?: DroneState,
    /** What is in the hand, and how many blows have been struck. See move. */
    held = 0,
    blows = 0,
    /** Whether they are in creative, so the server knows to leave them be. */
    flying = false,
  ): void {
    if (!this.online) return;
    const now = performance.now();
    if (now - this.sentAt < 1000 / INPUT_HZ) return;
    this.sentAt = now;
    /*
     * And the bag, if one was owed.
     *
     * A change that arrived inside the two seconds is remembered rather than
     * dropped: without this, the last thing you picked up before standing
     * still would be the one thing the world did not know about.
     */
    if (this.bagOwed !== null && now - this.bagAt >= 2000) {
      const owed = this.bagOwed;
      this.bagOwed = null;
      this.bagAt = now;
      this.send({ t: "bag", bag: owed });
    }
    this.send({
      t: "move", x: at.x, y: at.y, z: at.z, yaw, pitch,
      ...(drone !== undefined ? { drone } : {}),
      // Left out when there is nothing to say: an empty hand and a man who
      // has never swung are the common case, fifteen times a second.
      ...(held > 0 ? { held } : {}),
      ...(blows > 0 ? { blows } : {}),
      ...(flying ? { fly: true } : {}),
    });
  }

  /**
   * Tell the server what we grew, and let it refuse us if it differs.
   *
   * Called once, after the world exists. If the answer is a refusal the
   * socket closes and the game carries on alone, which is the right way round:
   * playing by yourself beats playing in a world nobody else can see.
   */
  ready(seed: number, sizeXZ: number, sizeY: number): void {
    this.send({ t: "ready", seed, sizeXZ, sizeY });
  }

  place(x: number, y: number, z: number, id: number): void {
    this.send({ t: "place", x, y, z, id });
  }

  /** Everything that is neither placing nor breaking. See the protocol. */
  set(x: number, y: number, z: number, id: number): void {
    this.send({ t: "set", x, y, z, id });
  }

  break(x: number, y: number, z: number): void {
    this.send({ t: "break", x, y, z });
  }

  /**
   * What we are carrying, for the world to keep.
   *
   * Rate-limited here rather than at the call site: a bag changes on every
   * block placed and every stack picked up, and a message each would be a
   * hundred a minute of the same list. Two seconds is faster than anybody
   * loses a connection on purpose.
   */
  bag(bag: BagState): void {
    if (!this.online) return;
    const now = performance.now();
    if (now - this.bagAt < 2000) { this.bagOwed = bag; return; }
    this.bagAt = now;
    this.bagOwed = null;
    this.send({ t: "bag", bag });
  }

  /** The last bag that was too soon to send. Flushed by `move`. */
  private bagOwed: BagState | null = null;
  private bagAt = 0;

  /** A spark off a кресало, at the cell it was struck into. */
  spark(x: number, y: number, z: number): void {
    this.send({ t: "spark", x, y, z });
  }

  /*
   * The човен, in five words.
   *
   * Where it *is* is never sent: while somebody is in a boat the boat is
   * where they are, and `move` already says that. Only the hull's heading
   * needs a message of its own.
   */
  launch(x: number, z: number): void { this.send({ t: "launch", x, z }); }
  board(id: number): void { this.send({ t: "board", id }); }
  row(yaw: number): void { this.send({ t: "row", yaw }); }
  ashore(): void { this.send({ t: "ashore" }); }
  scuttle(id: number): void { this.send({ t: "scuttle", id }); }

  /* The танк: the same five verbs a човен has, plus the gun. */
  deploy(x: number, y: number, z: number, yaw: number): void {
    this.send({ t: "deploy", x, y, z, yaw });
  }
  mount(id: number): void { this.send({ t: "mount", id }); }
  steer(yaw: number): void { this.send({ t: "steer", yaw }); }
  dismount(): void { this.send({ t: "dismount" }); }
  fireShell(
    x: number, y: number, z: number,
    vx: number, vy: number, vz: number, from: number,
  ): void {
    this.send({ t: "shell", x, y, z, vx, vy, vz, from });
  }

  /** A вогняний снаряд, as where it left from and which way it went. */
  bolt(
    from: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
  ): void {
    this.send({
      t: "bolt", x: from.x, y: from.y, z: from.z,
      dx: dir.x, dy: dir.y, dz: dir.z,
    });
  }

  /**
   * A blow at one of the server's orcs.
   *
   * The id is the server's; the damage is whatever is in our hand. Whether we
   * were near enough to swing is the server's to decide.
   */
  strike(kind: CreatureKind, id: number, damage: number): void {
    this.send({ t: "strike", kind, id, damage });
  }

  /** An arrow, as where it left from and which way it went. See `bolt`. */
  shoot(
    from: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
  ): void {
    this.send({
      t: "shoot", x: from.x, y: from.y, z: from.z,
      dx: dir.x, dy: dir.y, dz: dir.z,
    });
  }

  /** The lobby: make a team, join one, and start the match. */
  makeTeam(name: string): void { this.send({ t: "team", make: name }); }
  joinTeam(team: number): void { this.send({ t: "team", join: team }); }
  begin(): void { this.send({ t: "begin" }); }
  /**
   * "I have just died, and this is who did it."
   *
   * The server cannot see it happen - сила is the page's - and a scoreboard
   * that nobody tells about a death has nothing on it.
   */
  died(by: number): void { this.send({ t: "died", by }); }

  raise(building: string, facing: number, x: number, y: number, z: number): void {
    this.send({ t: "raise", building, facing, x, y, z });
  }

  private send(message: object): void {
    if (!this.online) return;
    this.wire?.send(message);
  }

  close(): void {
    try { this.wire?.close(); } catch { /* already gone */ }
    this.wire = null;
    this.joined = null;
  }
}
